import {
  ContextualMenu,
  IContextualMenuItem,
  mergeStyles,
} from "@fluentui/react";

import { isExternalOrReadonlyAdmin } from "form/util";
import { useTransferTempVehicle } from "hooks/data/mutations/useTransferTempVehicle";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { UserRole } from "types/types";
import { routes } from "../../../../config/routes";
import { setRemoveTEMPVehicle } from "../../../../store/fleetPage";
import { useSelector } from "../../../../store/hooks";
import { AppDispatch } from "../../../../store/store";

type Props = {
  vehicle: any;
  fleetId: number;
  onClickChangeStatus: () => void;
  onClickChangeIssuingStatus: () => void;
  onClickApproveStatus: () => void;
  onClickCancelStatus: () => void;
  isUpdateVehicleStatusLoading: boolean;
  isApproveVehicleStatusLoading: boolean;
  target: any;
  onDismiss: any;
};

const ContextualMenuItem = ({
  vehicle,
  fleetId,
  onDismiss,
  target,
  onClickChangeStatus,
  onClickChangeIssuingStatus,
  onClickApproveStatus,
  onClickCancelStatus,
  isUpdateVehicleStatusLoading,
  isApproveVehicleStatusLoading,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const taxonomy = useSelector((s) => s.taxonomy);

  const userRole = useSelector((s) => s.auth.userRole);
  const { mutate: onTransferTempVehicle, isLoading } = useTransferTempVehicle();

  const vehicleStatusTaxCode =
    taxonomy.VehicleStatusCode.byId[vehicle.vehicleStatusCode]?.code;
  const appSettings = vehicle.appSettings;

  let items: IContextualMenuItem[] = [
    {
      key: "open",
      text: t("bfm.openInNewTab.label"),
      onClick: () => {
        window.open(
          routes[
            vehicle.isTEMP ? "editTempVehicle" : "readOnlyVehicle"
          ].getPath(vehicle?.fleetId, vehicle?.vehicleId)
        );
      },
      disabled: isUpdateVehicleStatusLoading,
    },
    {
      key: "edit",
      text: t("bfm.editVehicle.label"),
      onClick: () => {
        if (vehicle.isTEMP) {
          navigate(
            routes.editTempVehicle.getPath(vehicle.fleetId, vehicle.vehicleId)
          );
        }
        //
        else {
          navigate(
            routes.editVehicle.getPath(vehicle.fleetId, vehicle.vehicleId)
          );
        }
      },

      disabled:
        isUpdateVehicleStatusLoading || isExternalOrReadonlyAdmin(userRole),
    },
    {
      key: "duplicate",
      text: t("bfm.duplicateVehicle.label"),
      onClick: () => {
        navigate(
          routes.duplicateVehicle.getPath(vehicle.fleetId, vehicle.vehicleId)
        );
      },
      className: vehicle.isTEMP ? displayNoneClass : "",
      disabled:
        isUpdateVehicleStatusLoading || isExternalOrReadonlyAdmin(userRole),
    },
    {
      key: "changeStatus",
      text: t("bfm.changeStatus.label"),
      onClick: onClickChangeStatus,
      className: vehicle.isTEMP ? displayNoneClass : "",
      disabled:
        (userRole === UserRole.external &&
          [
            "VehicleStatusCode.PENDING_FOR_ACTIVATION",
            "VehicleStatusCode.PENDING_FOR_SUSPENSION",
            "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
          ].includes(vehicleStatusTaxCode)) ||
        userRole === UserRole.adminreadonly ||
        ["VehicleStatusCode.DEACTIVATED"].includes(vehicleStatusTaxCode) ||
        isUpdateVehicleStatusLoading ||
        (!isExternalOrReadonlyAdmin(userRole) &&
          appSettings?.INTERNAL_USER_CAN_CHANGE_VEHICLE_STATUS === false),
    },

    {
      key: "transfer",
      text: t("bfm.transferVehicle.label"),
      onClick: (e) => {
        e.preventDefault();

        onTransferTempVehicle(
          {
            fleetId: vehicle.fleetId,
            vehicleId: vehicle.vehicleId,
          },
          {
            onSuccess: (res) => {
              dispatch(setRemoveTEMPVehicle(res.data));
              toast.success(t("bfm.notification.transfer.success"));
              onDismiss();
            },
          }
        );
      },
      disabled:
        isUpdateVehicleStatusLoading || isExternalOrReadonlyAdmin(userRole),
      className: !vehicle.isTEMP ? displayNoneClass : "",
    },

    {
      key: "approveStatus",
      text: t("bfm.approveStatus"),
      onClick: onClickApproveStatus,
      disabled: isApproveVehicleStatusLoading,
      className:
        isExternalOrReadonlyAdmin(userRole) ||
        vehicle.isTEMP ||
        ![
          "VehicleStatusCode.PENDING_FOR_ACTIVATION",
          "VehicleStatusCode.PENDING_FOR_SUSPENSION",
          "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
        ].includes(vehicleStatusTaxCode)
          ? displayNoneClass
          : "",
    },
    {
      key: "cancelStatus",
      text: t("bfm.cancelStatus"),
      onClick: onClickCancelStatus,
      disabled: isApproveVehicleStatusLoading,
      className:
        userRole == UserRole.adminreadonly ||
        vehicle.isTEMP ||
        ![
          "VehicleStatusCode.PENDING_FOR_ACTIVATION",
          "VehicleStatusCode.PENDING_FOR_SUSPENSION",
          "VehicleStatusCode.PENDING_FOR_DEACTIVATION",
        ].includes(vehicleStatusTaxCode)
          ? displayNoneClass
          : "",
    },
  ];

  return (
    <ContextualMenu
      items={items}
      target={target}
      gapSpace={10}
      onDismiss={onDismiss}
    />
  );
};

export default ContextualMenuItem;

const displayNoneClass = mergeStyles({
  display: "none",
});
